import React, { useState, useCallback } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Backdrop
} from '@mui/material';
import FestojoFormStepper from './FestojoFormStepper';
import BasicInfoForm from './BasicInfoForm';
import AppearanceForm from './AppearanceForm';
import SectionsForm from './SectionsForm';
import ReviewForm from './ReviewForm';
import { 
  FestojoFormData, 
  defaultFestojoData, 
  generateSlug 
} from '../../types/festejo';
import festojoService from '../../services/festojoService';

interface FormStep {
  id: number;
  label: string;
  component: React.ComponentType<StepComponentProps>;
}

interface StepComponentProps {
  formData: FestojoFormData;
  onUpdate: (data: Partial<FestojoFormData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const steps: FormStep[] = [
  { id: 1, label: 'Informações Básicas', component: BasicInfoForm },
  { id: 2, label: 'Aparência', component: AppearanceForm },
  { id: 3, label: 'Seções do Festejo', component: SectionsForm },
  { id: 4, label: 'Revisão e Publicação', component: ReviewForm }
];

const FestojoFormContainer: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [formData, setFormData] = useState<FestojoFormData>(defaultFestojoData);
  const [loading, setLoading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Atualizar dados do formulário
  const handleUpdateFormData = useCallback((newData: Partial<FestojoFormData>) => {
    setFormData(prevData => {
      const updatedData = { ...prevData, ...newData };
      
      // Auto-gerar slug quando o título mudar
      if (newData.title && newData.title !== prevData.title) {
        updatedData.slug = generateSlug(newData.title);
      }
      
      return updatedData;
    });
  }, []);

  // Navegar para próxima etapa
  const handleNext = useCallback(async () => {
    // Validar dados da etapa atual antes de prosseguir
    const validation = festojoService.validateFestojoData(formData);
    
    if (currentStep === 0 && !validation.isValid) {
      setSnackbar({
        open: true,
        message: validation.errors.join(', '),
        severity: 'error'
      });
      return;
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, formData]);

  // Navegar para etapa anterior
  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  // Salvar rascunho
  const handleSaveDraft = useCallback(async () => {
    setLoading(true);
    try {
      const response = await festojoService.saveDraft(formData);
      
      if (response.success && response.data) {
        setFormData(prev => ({ ...prev, id: response.data!.id }));
        setSnackbar({
          open: true,
          message: 'Rascunho salvo com sucesso!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Erro ao salvar rascunho:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar rascunho. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [formData]);

  // Publicar festejo
  const handlePublish = useCallback(async () => {
    setLoading(true);
    try {
      const validation = festojoService.validateFestojoData(formData);
      
      if (!validation.isValid) {
        setSnackbar({
          open: true,
          message: `Corrija os seguintes erros: ${validation.errors.join(', ')}`,
          severity: 'error'
        });
        return;
      }

      const response = await festojoService.publishFestejo(formData);
      
      if (response.success && response.data) {
        setSnackbar({
          open: true,
          message: 'Festejo publicado com sucesso!',
          severity: 'success'
        });
        
        // Opcional: redirecionar para página do festejo
        setTimeout(() => {
          window.open(`/festejo/${response.data!.slug}`, '_blank');
        }, 2000);
      }
    } catch (error) {
      console.error('Erro ao publicar festejo:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao publicar festejo. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [formData]);

  // Fechar snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Componente da etapa atual
  const CurrentStepComponent = steps[currentStep].component;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        {/* Cabeçalho */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Criar Novo Festejo
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center">
            Preencha as informações para criar o site do seu festejo religioso
          </Typography>
        </Box>

        {/* Stepper */}
        <FestojoFormStepper 
          steps={steps.map(step => step.label)}
          activeStep={currentStep}
        />

        {/* Conteúdo da etapa atual */}
        <Box sx={{ mt: 4 }}>
        <CurrentStepComponent
          formData={formData}
          onUpdate={handleUpdateFormData}
          onNext={handleNext}
          onPrevious={handlePrevious}
          isFirstStep={currentStep === 0}
          isLastStep={currentStep === steps.length - 1}
          {...(currentStep === steps.length - 1
            ? {
                onSaveDraft: handleSaveDraft,
                onPublish: handlePublish
              }
            : {})}
        />
      </Box>
      </Paper>

      {/* Loading backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Snackbar para notificações */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FestojoFormContainer;
export type { StepComponentProps };

